import React, { useEffect } from "react";
import { Box } from "@mui/material";
import {
  AlertSpg,
  BorderGreyBox,
  handleSubmitValidationFail,
  isNotEmpty,
  useReactHookForm,
} from "@duosoftbg/nacid-components";
import { useTranslation } from "react-i18next";
import { useStore } from "react-redux";
import { FormProvider } from "react-hook-form";
import { ivalARNacidSearch, IValARNacidSearch } from "../../../../../../config/initialFormValues";
import { vARNacidSearchTable } from "../../../../../../yup/schemas";
import { searchAcademicRecognitionNacid } from "../../../../../../axios/api/services";
import useSearchTableControl from "../../../../../../hooks/registers/search/useSearchTableControl";
import {
  SEARCH_FILTERS_GROUP,
  SYNC_GROUPS_CONFIG,
} from "../../../../../../config/registers/search/filters/groupsConfig";
import SearchFilters from "../../base/filters/SearchFilters";
import RecordNumberFilter from "../../base/filters/definition/RecordNumberFilter";
import ApplicantFilter from "../../base/filters/definition/ApplicantFilter";
import ApplicationDateFilter from "../../base/filters/definition/ApplicationDateFilter";
import RSearchAcademicRecognitionNacidList from "./RSearchAcademicRecognitionNacidList";
import DecisionNumberFilter from "../../base/filters/definition/DecisionNumberFilter";
import RecognizedSpecialityFilter from "../../base/filters/definition/RecognizedSpecialityFilter";
import ForeignUniversityFilter from "../../base/filters/definition/ForeignUniversityFilter";
import ARNacidStatusFilter from "../../base/filters/definition/ARNacidStatusFilter";
import UniversityCountryNameFilter from "../../base/filters/definition/UniversityCountryNameFilter";
import EduLevelFilter from "../../base/filters/definition/EduLevelFilter";
import { RootState } from "../../../../../../store/redux/store";

const RSearchAcademicRecognitionNacid = () => {
  const { t } = useTranslation();
  const store = useStore();
  const group = SEARCH_FILTERS_GROUP.ACADEMIC_RECOGNITION_NACID;

  const { methods } = useReactHookForm<IValARNacidSearch>({
    defaultValues: ivalARNacidSearch,
    validationSchema: vARNacidSearchTable,
  });

  useEffect(() => {
    const filtersData = (store.getState() as RootState).SearchData.searchTable[group].filtersData;
    if (isNotEmpty(filtersData)) {
      methods.reset(filtersData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const control = useSearchTableControl({
    group,
    methods,
    initialValues: ivalARNacidSearch,
    filterData: searchAcademicRecognitionNacid,
    callAfterResetFilters: true,
    syncGroups: {
      groups: [SEARCH_FILTERS_GROUP.ACADEMIC_RECOGNITION_UNIVERSITIES],
      config: SYNC_GROUPS_CONFIG.ARSyncConfig,
    },
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(
          () => control.handleSubmitFilters(),
          () => handleSubmitValidationFail(methods.formState, t, undefined),
        )}
      >
        <Box>
          <BorderGreyBox>
            <SearchFilters
              isSubmitBtnDisabled={control.isSubmitBtnDisabled}
              isResetBtnDisabled={control.isResetBtnDisabled}
              handleResetFilters={control.handleResetFilters}
            >
              <ApplicantFilter />
              <RecognizedSpecialityFilter />
              <ForeignUniversityFilter />
              <ARNacidStatusFilter />
              <UniversityCountryNameFilter />
              <RecordNumberFilter />
              <ApplicationDateFilter />
              <DecisionNumberFilter />
              <EduLevelFilter group={group} />
              <AlertSpg mt={5} ml={4} severity={"info"}>
                <span dangerouslySetInnerHTML={{ __html: t("m.info.ar.nacid") }} />
              </AlertSpg>
            </SearchFilters>
          </BorderGreyBox>
          {control.showList && (
            <RSearchAcademicRecognitionNacidList
              records={control.records}
              total={control.total}
              isLoading={control.isLoading}
              onPageOrOrderChange={control.handlePageOrOrderChange}
              blockTable={control.blockTable}
              group={group}
              viewURL={"/academic-recognitions/view?id="}
            />
          )}
        </Box>
      </form>
    </FormProvider>
  );
};

export default RSearchAcademicRecognitionNacid;
